import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isDesktop } from 'react-device-detect';
import useRouter from './useRouter';
import {
  checkifHaveContentAccess,
  openStore,
} from '../utils/validationUserUtils';
import {
  convertPath,
  getPathNameByContentType,
} from '../utils/pathRoutesUtils';
import {
  resolveNavigationDestiny,
  validateRedirect,
  validateUrlRedirect,
  validateAccessToContentByUrns,
} from '../utils/ContentUtils';
import { CONTENT_TYPES } from '../constants/contentTypes';
import { redirectToAccountInformation } from '../containers/Navbar/actions';
import { validateLiveContent } from '@tbx/experience-widgets-lib';
import { hasRedirectToStores } from '@tbx/utils/responsiveWebUtils';
import { trackPaywallCtaClick } from '../utils/singularUtils';

/**
 * useContentNavigation
 * Simplifies and centralizes content navigation logic decoupled from sections.
 * @param {Object} options Configuration and Redux dependencies
 */
export default function useContentNavigation(options = {}) {
  const {
    authenticated,
    accessToken,
    PATHS,
    mobileAppUrls,
    hasPayService,
    hasDirectAccessToAddOns,
    enableContentPage,
    packageUpgradeMessages,
    redirectUrl, // The URL returned by the getContentsUrlById action/saga
    redirectError, // The error (e.g., CS-011) from getContentsUrlById action/saga
    onGetContentsUrlById, // Callback to dispatch getContentsUrlById
    onGetEpisodeBySerie, // Callback to get the lowest/bookmarked episode ID
    currentContentPosition,
    enabledResponsiveWeb,
    onNavigate,
    selectUserUrns,
    toastAdd, // Toast notification callback for errors
    singularEventsConfig, // client's useSingular.events, used to gate paywall_cta_click
    sectionName, // Singular section_name attached to paywall_cta_click
  } = options;

  const router = useRouter();
  const dispatch = useDispatch();
  const newWindowRef = useRef(null);
  const pendingContentRef = useRef(null);

  // Handle CS-011 Specific flow (Upsells / Upgrades)
  const handleNoAccessFlow = useCallback(
    (content, contentId) => {
      trackPaywallCtaClick(
        {
          content_id: content?.id,
          title: content?.title,
          content_type: content?.contentType,
          urn: content?.urn,
          section_name: sectionName || null,
        },
        singularEventsConfig,
      );

      const cs011Match = packageUpgradeMessages?.find(
        (r) => r.urn === content?.urn && !!r.url,
      );

      if (!hasPayService && cs011Match) {
        window.open(cs011Match.url, cs011Match.target || '_blank');
      } else {
        dispatch(
          redirectToAccountInformation(accessToken, hasDirectAccessToAddOns),
        );
      }
    },
    [
      hasPayService,
      packageUpgradeMessages,
      accessToken,
      hasDirectAccessToAddOns,
      dispatch,
      singularEventsConfig,
      sectionName,
    ],
  );

  // Handle Safari/Chrome Popup Blocker safely with synchronous window creation
  useEffect(() => {
    if (redirectUrl && pendingContentRef.current) {
      newWindowRef.current = window.open(redirectUrl, '_blank');
      pendingContentRef.current = null;
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (redirectError && pendingContentRef.current) {
      if (newWindowRef.current && !newWindowRef.current.closed) {
        newWindowRef.current.close();
      }
      newWindowRef.current = null;
      pendingContentRef.current = null;
      if (toastAdd) {
        toastAdd(redirectError);
      }
    }
  }, [redirectError, toastAdd]);

  /**
   * Main Click Handler for "Ver Ahora" (Play Button) or clicking an Episode Item.
   */
  const handlePlayContent = useCallback(
    (content, isEpisodeList = false, { zappingOrigin = null } = {}) => {
      if (hasRedirectToStores(isDesktop)) {
        openStore(mobileAppUrls);
        return;
      }

      const hasRedirect =
        validateRedirect(content) ||
        (isEpisodeList && validateUrlRedirect(currentContentPosition));

      // Access Gates (must check BEFORE Serie-specific logic)
      const hasAccess = checkifHaveContentAccess(authenticated, content);
      if (!hasAccess) {
        if (!authenticated) {
          const { origin } = window.location;
          const subPath =
            getPathNameByContentType(content?.contentType, PATHS) ||
            PATHS.content;
          const returnURL = origin + convertPath(subPath, content?.id);
          router.push(PATHS.login, { returnURL });
          return;
        }
        handleNoAccessFlow(content, content?.id);
        return;
      }

      const canPlay = content?.free
        ? true
        : validateAccessToContentByUrns(content?.urn, selectUserUrns);

      if (!canPlay) {
        const isDirectToPlayer =
          content?.contentType === CONTENT_TYPES.BROADCAST ||
          content?.contentType === CONTENT_TYPES.EPISODE;
        if (!isDirectToPlayer) {
          handleNoAccessFlow(content, content?.id);
          return;
        }
      }

      if (
        content?.contentType === CONTENT_TYPES.SERIE &&
        !isEpisodeList &&
        onGetEpisodeBySerie
      ) {
        onGetEpisodeBySerie(content.id, hasRedirect, content);
        return;
      }

      // Handle Redirect explicitly
      if (hasRedirect) {
        if (!authenticated) {
          const { origin } = window.location;
          const subPath =
            getPathNameByContentType(content?.contentType, PATHS) ||
            PATHS.content;
          const returnURL = origin + convertPath(subPath, content?.id);
          router.push(PATHS.login, { returnURL });
          return;
        }
        if (onGetContentsUrlById) {
          pendingContentRef.current = content;
          onGetContentsUrlById(content?.id);
          return;
        }
      }

      // Resolve where to go: Player or Ficha
      let targetId = content?.id;

      if (
        currentContentPosition?.bookmarkEpisodes?.length > 0 &&
        content?.contentType === CONTENT_TYPES.SERIE
      ) {
        targetId =
          currentContentPosition.bookmarkEpisodes[0].contentId || targetId;
      } else if (currentContentPosition?.currentContent && !isEpisodeList) {
        targetId = currentContentPosition.currentContent.id || targetId;
      }

      if (!targetId) {
        console.warn(
          '[useContentNavigation] handlePlayContent: targetId is undefined. Aborting navigation.',
        );
        return;
      }

      onNavigate?.();
      router.push(convertPath(PATHS.player, targetId), { zappingOrigin });
    },
    [
      authenticated,
      mobileAppUrls,
      currentContentPosition,
      onGetContentsUrlById,
      PATHS,
      enableContentPage,
      handleNoAccessFlow,
      onGetEpisodeBySerie,
      router,
      onNavigate,
      selectUserUrns,
    ],
  );

  /**
   * Action when clicking a general Home/Section item or Banner.
   */
  const handleSelectContent = useCallback(
    (content, { isBookmark = false, zappingOrigin = null } = {}) => {
      if (hasRedirectToStores(isDesktop) && validateLiveContent(content)) {
        openStore(mobileAppUrls);
        return;
      }

      const hasRedirect = validateRedirect(content);

      // Redirect Flow
      if (hasRedirect) {
        if (!authenticated) {
          const { origin } = window.location;
          const returnURL = origin + convertPath(PATHS.player, content?.id);
          router.push(PATHS.login, { returnURL });
          return;
        }
        if (onGetContentsUrlById) {
          pendingContentRef.current = content;
          onGetContentsUrlById(content?.id);
          return; // Early return to avoid player navigation
        }
      }

      const hasAccess = checkifHaveContentAccess(authenticated, content);

      // Ficha vs Player Routing
      if (
        isBookmark ||
        resolveNavigationDestiny(content, enableContentPage) === 'PLAYER'
      ) {
        if (hasAccess) {
          onNavigate?.();
          router.push(convertPath(PATHS.player, content?.id), {
            zappingOrigin,
          });
        } else {
          const { origin } = window.location;
          const returnURL = origin + convertPath(PATHS.player, content?.id);
          router.push(PATHS.login, { returnURL });
        }
        return;
      }

      // Else go to Ficha
      const subPath =
        getPathNameByContentType(content?.contentType, PATHS) || PATHS.content;
      if (content?.id) {
        onNavigate?.();
        router.push(convertPath(subPath, content?.id));
      }
    },
    [
      authenticated,
      mobileAppUrls,
      enableContentPage,
      enabledResponsiveWeb,
      onGetContentsUrlById,
      onNavigate,
      PATHS,
      router,
    ],
  );

  return {
    handlePlayContent,
    handleSelectContent,
  };
}
